import { useEffect } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Quagga from 'quagga'

import { FlexContainer } from 'components/Flex'

export default function JanReader({ open, onClose, onDetect }) {
  useEffect(() => {
    const handleDetected = result => {
      const { code } = result.codeResult
      if (/^4[59]([0-9]{11}|[0-9]{6})$/.test(code)) {
        onDetect(code)
        onClose()
      }
    }
    Quagga.onDetected(handleDetected)
    return () => Quagga.offDetected(handleDetected)
  }, [onDetect, onClose])

  const setStyle = () => {
    document.querySelector('#interactive canvas').remove()
    const video = document.querySelector('#interactive video')
    const cssText = 'width: 100%; height: auto'
    video.style.cssText = cssText
  }

  const handleEnter = () => {
    Quagga.init(
      {
        inputStream: { type: 'LiveStream' },
        decoder: { readers: ['ean_reader'] },
        locate: false,
        frequency: 3,
        numOfWorkers: 1,
      },
      error => {
        if (error) {
          return console.error(error)
        }
        setStyle()
        Quagga.start()
      }
    )
  }

  const handleExited = () => {
    Quagga.stop()
    onClose()
  }

  return (
    <Dialog
      open={open}
      TransitionProps={{ onEnter: handleEnter, onExited: handleExited }}
      fullScreen
    >
      <FlexContainer
        id="interactive"
        className="viewport"
        style={{ alignItems: 'center', justifyContent: 'center', background: '#000' }}
      />
      <Guide />
      <IconButton
        style={{ position: 'absolute', top: 0, right: 0, color: '#fff' }}
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>
    </Dialog>
  )
}

const Guide = () => {
  const gridTemplate = () => {
    const jancodeAspectRatio = 22.86 / 37.29 // https://raksul.com/magazine/column/barcode-size/
    const w = window.innerWidth
    const h = window.innerHeight
    if (h / w > jancodeAspectRatio) {
      // スマホ縦向きを想定
      const r = 0.8 // ヘルパーの div の画面に対する幅の割合
      return {
        row: `auto ${parseInt(w * r * jancodeAspectRatio)}px auto`,
        column: `${(1 - r) / 2}fr ${r}fr ${(1 - r) / 2}fr`,
      }
    } else {
      // スマホ横向きを想定
      const r = 0.6 // ヘルパーの div の画面に対する高さの割合
      return {
        row: `${(1 - r) / 2}fr ${r}fr ${(1 - r) / 2}fr`,
        column: `auto ${parseInt((h * r) / jancodeAspectRatio)}px auto`,
      }
    }
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'grid',
        gridTemplateColumns: gridTemplate().column,
        gridTemplateRows: gridTemplate().row,
        gap: 0,
        height: '100%', // Safari 対応: https://stackoverflow.com/questions/44770074/css-grid-row-height-safari-bug
      }}
    >
      {[...Array(5).keys()].map(index => {
        /*
         * Grid Layout
         * |     0     |
         * | 1 | 2 | 3 |
         * |     4     |
         */
        switch (index) {
          case 0:
            return (
              <div
                key={index}
                style={{
                  backdropFilter: 'blur(4px) brightness(70%)',
                  gridColumnStart: 1,
                  gridColumnEnd: 4,
                }}
              />
            )
          case 1:
          case 3:
            return <div key={index} style={{ backdropFilter: 'blur(4px) brightness(70%)' }} />
          case 2:
            return <div key={index} style={{ border: '5px solid rgba(255, 255, 255, 0.7)' }} />
          case 4:
            return (
              <Box
                key={index}
                sx={{
                  backdropFilter: 'blur(4px) brightness(70%)',
                  textAlign: 'center',
                  pt: 3,
                  gridColumnStart: 1,
                  gridColumnEnd: 4,
                }}
              >
                <Typography
                  sx={{
                    color: '#fff',
                    background: 'rgba(0, 0, 0, 0.7)',
                    py: 1,
                    px: 2,
                    borderRadius: 2,
                  }}
                  variant="caption"
                >
                  バーコードをかざしてください
                </Typography>
              </Box>
            )
          default:
            return null
        }
      })}
    </div>
  )
}
